<template>
  <section
    class="view-wrapper"
    :class="{
      'view-wrapper--scrollable': isScrollable,
      'view-wrapper--not-scrollable': !isScrollable,
      'view-wrapper--virtual-height': placeholderHeight,
    }"
    :style="{ overscrollBehaviorY: isScrollable ? 'contain' : false }"
  >
    <ButtonBase v-bind="logoProps" class="view-wrapper__logo">
      <img src="../../assets/images/sap_logo.png" />
    </ButtonBase>

    <transition name="fade" mode="out-in">
      <ButtonClose v-if="$listeners.close" class="view-wrapper__close" @click="$emit('close')" />
      <ButtonSoundToggle v-else-if="soundControllable" class="view-wrapper__sound-button" />
    </transition>

    <LanguageDropdown></LanguageDropdown>

    <div class="view-wrapper__content">
      <div
        v-if="$scopedSlots.header"
        class="view-wrapper__header"
        :class="[`view-wrapper__header-${headerOrientation}`]"
      >
        <slot name="header" />
      </div>

      <main v-if="$scopedSlots.default" class="view-wrapper__main">
        <slot />
      </main>

      <div
        v-if="$scopedSlots.footer"
        class="view-wrapper__footer"
        :class="[`view-wrapper__footer-${footerOrientation}`]"
      >
        <slot name="footer" />
      </div>
    </div>

    <div
      v-if="placeholderHeight"
      class="view-wrapper__height-placeholder"
      :style="{ height: placeholderHeight + '%' }"
    />

    <ProgressFrameBar v-if="hasIndicator" :indicator-percentage="indicatorPercentage" />
  </section>
</template>

<script>
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import ButtonBase from '../ButtonBase';
import ButtonClose from '../ButtonClose';
import ButtonSoundToggle from '../ButtonSoundToggle';
import ProgressFrameBar from '../ProgressFrameBar';

import LanguageDropdown from '../LanguageDropdown';

export default {
  name: 'ViewWrapper',

  components: {
    ButtonBase,
    ButtonClose,
    ButtonSoundToggle,
    ProgressFrameBar,
    LanguageDropdown,
  },

  props: {
    headerOrientation: {
      default: 'vertical',
      validator: value => ['horizontal', 'vertical'].includes(value),
    },

    footerOrientation: {
      default: 'horizontal',
      validator: value => ['horizontal', 'vertical'].includes(value),
    },

    indicatorPercentage: {
      type: Number,
      default: 0,
    },

    hasIndicator: {
      type: Boolean,
      default: false,
    },

    scrollSteps: {
      type: Number,
      default: undefined,
    },

    scrollable: {
      type: Boolean,
      default: undefined,
    },

    soundControllable: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    logoProps() {
      return this.$route.name === 'onboarding'
        ? {
            href: 'https://sap.com',
            target: '_blank',
          }
        : { to: { name: 'onboarding' } };
    },

    placeholderHeight() {
      if (!this.scrollSteps) return;
      return this.scrollSteps * 100;
    },

    isScrollable() {
      return this.scrollSteps || this.scrollable === true;
    },
  },

  mounted() {
    this.$watch(
      'isScrollable',
      isScrollable => {
        if (isScrollable) {
          enableBodyScroll(this.$el);
        } else {
          disableBodyScroll(this.$el);
        }
      },
      { immediate: true }
    );
  },

  beforeDestroy() {
    enableBodyScroll(this.$el);
  },

  methods: {
    LogoClicked(e) {
      if (this.$route.name === 'onboarding') {
        window.open('https://sap.com');
      } else {
        e.preventDefault();
        this.$router.replace({ name: 'onboarding' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.view-wrapper {
  position: fixed;
  height: 100%;
  width: 100%;
  padding-bottom: env(safe-area-inset-bottom);
  box-sizing: border-box;
}

.view-wrapper--scrollable {
  position: relative;
  min-height: 100%;
  height: auto;
}

.view-wrapper--virtual-height {
  position: relative;
  height: 100%;
}

.view-wrapper__logo {
  width: 60px;
  position: fixed;
  left: 20px;
  top: calc(20px + env(safe-area-inset-top));

  z-index: 20;
  img {
    width: 60px;
  }
}

.view-wrapper__logo-label {
  @include visually-hidden;
}

.view-wrapper--scrollable:not(.view-wrapper--virtual-height) .view-wrapper__logo {
  position: absolute;
}

.view-wrapper__sound-button,
.view-wrapper__close {
  position: fixed;
  right: 0;
  top: env(safe-area-inset-top);

  z-index: 20;
}

.view-wrapper__content {
  position: relative;
  min-height: 100%;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 1fr max-content 1fr;

  /**
   * When the ViewWrapper contains a ScrollFade component then it starts
   * sometimes scrolling with the content or not. It depends on the scroll
   * speed. My (WF) conclusion was, that .view-wrapper__content sometimes
   * creates or acts like a second scroll container which is not what we want.
   * To avoid this behaviour, overflow: hidden is necessary. The result is,
   * that the inner elements stays in position without moving randamly around.
   */
  overflow: hidden;
}

.view-wrapper--virtual-height .view-wrapper__content {
  box-sizing: border-box;
  position: fixed;
  width: 100%;
  height: 100%;
}

.view-wrapper__main {
  grid-row: 2;
}

.view-wrapper--scrollable {
  .view-wrapper__header {
    position: sticky;
    top: 0;
  }
  .view-wrapper__footer {
    position: sticky;
    bottom: 0;
  }
}

.view-wrapper__header {
  grid-row: 1;
  align-self: start;
  display: flex;
  justify-content: center;
}

.view-wrapper__footer {
  grid-row: 3;
  align-self: end;
  display: flex;
  justify-content: center;
}

.view-wrapper__header-horizontal,
.view-wrapper__footer-horizontal {
  justify-content: center;
}

.view-wrapper__header-vertical,
.view-wrapper__footer-vertical {
  flex-direction: column;
  align-items: center;
}

*:fullscreen {
  /* ----------- iPad ----------- */
  /* iPad Portrait */
  @media screen and (min-device-width: 481px) and (max-device-width: 1025px) {
    .view-wrapper__logo {
      top: calc(80px + env(safe-area-inset-top));
    }
  }
}
</style>
